import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, 
  Shield, 
  FileCheck, 
  Users, 
  ArrowRight,
  CheckCircle2,
  Clock,
  DollarSign,
  Zap
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { llcAPI } from '@/services/api';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Default LLC pricing plans - defined outside component to avoid temporal dead zone
const defaultPricingPlans = [
  {
    name: 'Basic',
    price: 99,
    features: [
      'LLC formation in any state',
      'Name availability check',
      'Articles of Organization',
      'Basic operating agreement',
      'EIN application assistance'
    ],
    turnaround: '5-7 business days',
    popular: false
  },
  {
    name: 'Premium',
    price: 249,
    popular: true,
    features: [
      'Everything in Basic',
      'Registered agent service (1 year)',
      'Custom operating agreement',
      'Business banking resolution',
      'Compliance calendar',
      'Priority processing'
    ],
    turnaround: '2-3 business days'
  },
  {
    name: 'Platinum',
    price: 399,
    features: [
      'Everything in Premium',
      'Registered agent (lifetime)',
      'Business license research',
      'Tax consultation (30 min)',
      'Annual report filing (1 year)',
      'Same-day processing'
    ],
    turnaround: '24 hours',
    popular: false
  }
];

export default function LLCCreationDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pricingPlans, setPricingPlans] = useState(defaultPricingPlans);
  const [myLLCs, setMyLLCs] = useState([]);
  const [loadingLLCs, setLoadingLLCs] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleStartLLC = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmLLC = () => {
    setShowConfirmDialog(false);
    navigate('/llc/create');
  };

  const handleCancelLLC = () => {
    setShowConfirmDialog(false);
  };

  // Load LLC pricing from localStorage
  useEffect(() => {
    const storedPlans = localStorage.getItem('llcPricingPlans');
    if (storedPlans) {
      const plans = JSON.parse(storedPlans);
      setPricingPlans(plans.filter(p => p.active));
    }
    // If no stored plans, component already initialized with defaultPricingPlans
  }, []);

  // Fetch user's LLC formations
  useEffect(() => {
    const fetchLLCs = async () => {
      try {
        setLoadingLLCs(true);
        const data = await llcAPI.getLLCs();
        // Show all LLCs (including drafts) sorted by date
        const sortedLLCs = data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setMyLLCs(sortedLLCs);
      } catch (error) {
        console.error('Error fetching LLC formations:', error);
        // Don't show error toast on dashboard - just fail silently
      } finally {
        setLoadingLLCs(false);
      }
    };

    if (user) {
      fetchLLCs();
    }
  }, [user]);

  if (!user) {
    return <div>Loading...</div>;
  }

  const features = [
    {
      icon: Shield,
      title: 'Asset Protection',
      description: 'Protect your personal assets from business liabilities'
    },
    {
      icon: FileCheck,
      title: 'Professional Image',
      description: 'Build credibility with customers and partners'
    },
    {
      icon: DollarSign,
      title: 'Tax Benefits',
      description: 'Flexible tax options and deductions'
    },
    {
      icon: Users,
      title: 'Easy Management',
      description: 'Simple ownership structure and operations'
    }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      'DRAFT': { label: 'Draft', className: 'bg-gray-500' },
      'PENDING': { label: 'Pending', className: 'bg-yellow-500' },
      'SUBMITTED': { label: 'Submitted', className: 'bg-blue-500' },
      'IN_REVIEW': { label: 'In Review', className: 'bg-yellow-500' },
      'IN_PROGRESS': { label: 'In Progress', className: 'bg-purple-500' },
      'PROCESSING': { label: 'Processing', className: 'bg-purple-500' },
      'APPROVED': { label: 'Approved', className: 'bg-green-500' },
      'COMPLETED': { label: 'Completed', className: 'bg-green-600' },
      'REJECTED': { label: 'Rejected', className: 'bg-red-500' },
    };
    return statusConfig[status] || { label: status, className: 'bg-gray-500' };
  };

  return (
    <DashboardLayout role={user.role} title="LLC Dashboard" user={user}>
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'LLC Dashboard' }]} />

        {/* Hero Section */}
        <div className="rounded-xl bg-gradient-to-r from-primary/10 to-secondary/10 p-8">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-primary text-primary-foreground">
              <Building2 className="h-3 w-3 mr-1" />
              Business Formation
            </Badge>
            <h1 className="text-4xl font-bold text-foreground mb-4">
              Form Your LLC in Minutes
            </h1>
            <p className="text-lg text-muted-foreground mb-6">
              Start your business with confidence. We handle all the paperwork and legal requirements.
            </p>
            <Button 
              size="lg" 
              className="btn-professional"
              onClick={handleStartLLC}
            >
              Start LLC Formation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Quick Actions & LLC Formations */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                className="w-full justify-between"
                onClick={handleStartLLC}
              >
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Start New LLC Formation
                </span>
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-between"
                onClick={() => navigate('/llc/filing-status')}
              >
                Check Filing Status
                <FileCheck className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>

          {/* Your LLC Formations */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Your LLC Formations</CardTitle>
                {myLLCs.length > 2 && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/llc/requests')}
                  >
                    View All
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loadingLLCs ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                </div>
              ) : myLLCs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No LLC formations yet</p>
                  <p className="text-sm mt-2">Start a new LLC formation to get started</p>
                  <Button 
                    className="mt-4"
                    onClick={handleStartLLC}
                  >
                    <Building2 className="h-4 w-4 mr-2" />
                    Start New Formation
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {myLLCs.slice(0, 2).map((llc) => {
                    const statusBadge = getStatusBadge(llc.status);
                    
                    return (
                      <div
                        key={llc.id}
                        className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/llc/requests/${llc.id}`)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-primary" />
                            <p className="font-medium">{llc.company_name || 'Unnamed LLC'}</p>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {llc.state} • Created {llc.created_at ? new Date(llc.created_at).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={`${statusBadge.className} text-white`}>
                            {statusBadge.label}
                          </Badge>
                          <Button variant="ghost" size="sm">
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Why Form an LLC */}
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-4">Why Form an LLC?</h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="tax-card">
                  <CardContent className="pt-6">
                    <div className="mb-4 inline-flex rounded-xl bg-primary/10 p-3">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="mb-2 text-lg font-semibold text-foreground">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Process Overview */}
        <Card className="tax-card">
          <CardHeader>
            <CardTitle>How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-4">
              {[
                { step: '1', title: 'Choose Package', description: 'Select the formation package that fits your needs' },
                { step: '2', title: 'Provide Details', description: 'Fill out simple forms with your business information' },
                { step: '3', title: 'We File', description: 'We prepare and file all required documents with the state' },
                { step: '4', title: 'Get Your LLC', description: 'Receive your official LLC documents and EIN' }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-xl font-bold text-primary-foreground">
                    {item.step}
                  </div>
                  <h3 className="mb-2 font-semibold text-foreground">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Dialog for New LLC Formation */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Certification & Consent - LLC Formation</AlertDialogTitle>
            <div className="space-y-4 text-sm text-left">
              <p className="font-medium text-foreground">
                Please read and acknowledge the following before proceeding:
              </p>
              
              <div className="space-y-3 bg-muted/50 p-4 rounded-lg border text-muted-foreground">
                <p>
                  <span className="font-medium">1.</span> I certify that all documents and information I submit are true, complete, and accurate to the best of my knowledge.
                </p>
                <p>
                  <span className="font-medium">2.</span> I authorize Kosha Tax Consultants to review, prepare, and file my LLC formation documents using the information I provide.
                </p>
                <p>
                  <span className="font-medium">3.</span> I understand that my documents contain sensitive personal and financial data and consent to their secure storage and processing.
                </p>
                <p>
                  <span className="font-medium">4.</span> I acknowledge that LLC formation approval and processing times are determined by state authorities and are not guaranteed.
                </p>
                <p>
                  <span className="font-medium">5.</span> I agree to respond promptly to requests for additional information. Delays caused by incomplete or missing documents are my responsibility.
                </p>
                <p>
                  <span className="font-medium">6.</span> I understand that Kosha Tax Consultants is not responsible for penalties, interest, or losses resulting from incorrect, incomplete, or misleading information provided by me.
                </p>
                <p>
                  <span className="font-medium">7.</span> I consent to receive LLC-related communications and documents electronically.
                </p>
                <p>
                  <span className="font-medium">8.</span> I agree that submitted documents may be retained for compliance, audit, and regulatory purposes.
                </p>
                <p>
                  <span className="font-medium">9.</span> I understand that submission of documents does not establish a client relationship until services are confirmed and payment is completed.
                </p>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="font-medium text-blue-900 dark:text-blue-100 mb-2">Data Privacy & Deletion Request</p>
                <p className="text-blue-800 dark:text-blue-200">
                  I understand that I may request deletion of my personal data, subject to legal and regulatory retention requirements, by contacting <a href="mailto:support@koshatax.com" className="underline font-medium">support@koshatax.com</a>.
                </p>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel onClick={handleCancelLLC}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmLLC}>I Agree & Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
